import { Injectable, signal, inject, Signal } from "@angular/core";
import { TranslocoService } from "@jsverse/transloco";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  public _translocoService = inject(TranslocoService);
  private readonly supportedLanguages = ["fr", "en", "es", "de", "cs", "pl"];
  private readonly currentLanguageSignal = signal<string>("en"); // Default to 'en'

  constructor() {
    this.initBrowserLanguage();
  }

  get currentLanguage(): Signal<string> {
    return this.currentLanguageSignal;
  }

  /**
   * Initialize the language based on browser settings
   */
  private initBrowserLanguage(): void {
    const browserLang = navigator.language?.split("-")[0]; // Get primary language code from browser
    const languageToUse = this.supportedLanguages.includes(browserLang)
      ? browserLang
      : "en";

    this.setLanguage(languageToUse);
  }

  /**
   * Change the application language
   * @param lang The language code to change to
   */
  setLanguage(lang: string): void {
    if (this.supportedLanguages.includes(lang)) {
      this._translocoService.setActiveLang(lang);
      this.currentLanguageSignal.set(lang);
    }
  }

  /**
   * Toggle between available languages
   */
  toggleLanguage(): void {
    const currentIndex = this.supportedLanguages.indexOf(this.currentLanguageSignal());
    const nextIndex = (currentIndex + 1) % this.supportedLanguages.length;
    this.setLanguage(this.supportedLanguages[nextIndex]);
  }

  /**
   * Get language name from language code
   */
  getLanguageName(langCode: string): string {
    const languageNames: Record<string, string> = {
      fr: "Français",
      en: "English",
      es: "Español",
      de: "Deutsch",
      cs: "Čeština",
      pl: "Polski",
    };

    return languageNames[langCode] || langCode;
  }
}
